import { useState, useEffect } from 'react'
import Navbar from '../components/Navbar'
import axios from 'axios'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Users, Clock, TrendingUp, AlertCircle, BarChart3, MessageCircle, ThumbsUp, ThumbsDown, Minus } from 'lucide-react'
import TrendingItemSkeleton from '../components/TrendingItemSkeleton'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

const Trending = () => {
  const { token, user } = useAuth()
  const [mostTrending, setMostTrending] = useState([])
  const [controversial, setControversial] = useState([])
  const [closeCall, setCloseCall] = useState([])
  const [selectedPost, setSelectedPost] = useState(null)
  const [postDetails, setPostDetails] = useState(null)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => { loadTrending() }, [])

  const loadTrending = async () => {
    try {
      const [most, cont, close] = await Promise.all([
        axios.get(`${API}/trending/most`),
        axios.get(`${API}/trending/controversial`),
        axios.get(`${API}/trending/close-call`),
      ])
      setMostTrending(most.data.posts || [])
      setControversial(cont.data.posts || [])
      setCloseCall(close.data.posts || [])
    } catch (err) {
      console.error('Error loading trending:', err)
    } finally {
      setLoading(false)
    }
  }

  const openPost = async (post) => {
    setSelectedPost(post)
    setPostDetails(null)
    if (!token) return
    if (!token) return
    setDetailsLoading(true)
    try {
      const res = await axios.get(`${API}/posts/${post.id}/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setPostDetails(res.data)
    } catch {
      // stats not available, show basic info from post
    } finally {
      setDetailsLoading(false)
    }
  }

  const getTimeAgo = (timestamp) => {
    const diff = Math.floor((Date.now() - new Date(timestamp)) / 1000)
    if (diff < 60) return `${diff}s ago`
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    return `${Math.floor(diff / 86400)}d ago`
  }

  const PostItem = ({ post, index }) => (
    <div
      className="flex items-start gap-4 p-4 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 hover:border-cyan-300 transition-colors cursor-pointer"
      onClick={() => openPost(post)}
    >
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-bold flex-shrink-0">
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2" onClick={e => e.stopPropagation()}>
          <img src={post.author_avatar} className="w-6 h-6 rounded-full" alt="" />
          <Link
            to={user && post.author_username === user.username ? '/profile' : `/profile/${post.author_username}`}
            className="text-sm font-medium text-gray-700 dark:text-white hover:underline hover:text-cyan-600"
          >
            {post.author_username}
          </Link>
          {post.author_role === 'premium' && <span className="text-yellow-500 text-xs">⭐</span>}
        </div>
        <p className="text-gray-900 dark:text-slate-100 mb-3 line-clamp-2">{post.content}</p>
        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-slate-400">
          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{post.vote_count || post.votes?.total || 0} votes</span>
          <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{post.comment_count || 0}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{getTimeAgo(post.created_at)}</span>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 w-full">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Trending</h1>
            <p className="text-gray-600 dark:text-slate-400">See what's hot and controversial</p>
          </div>

          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <TrendingItemSkeleton key={i} />)}
            </div>
          ) : (
            <Tabs defaultValue="most" className="w-full">
              <TabsList className="grid w-full max-w-md mx-auto grid-cols-3 mb-8 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
                <TabsTrigger value="most" className="flex items-center justify-center gap-1 text-gray-600 dark:text-slate-400 data-[state=active]:text-cyan-600 dark:data-[state=active]:text-cyan-400 data-[state=active]:bg-cyan-50 dark:data-[state=active]:bg-cyan-500/10 rounded-full">
                  <TrendingUp className="w-4 h-4" /><span className="hidden sm:inline">Most</span>
                </TabsTrigger>
                <TabsTrigger value="controversial" className="flex items-center justify-center gap-1 text-gray-600 dark:text-slate-400 data-[state=active]:text-cyan-600 dark:data-[state=active]:text-cyan-400 data-[state=active]:bg-cyan-50 dark:data-[state=active]:bg-cyan-500/10 rounded-full">
                  <AlertCircle className="w-4 h-4" /><span className="hidden sm:inline">Controversial</span>
                </TabsTrigger>
                <TabsTrigger value="close" className="flex items-center justify-center gap-1 text-gray-600 dark:text-slate-400 data-[state=active]:text-cyan-600 dark:data-[state=active]:text-cyan-400 data-[state=active]:bg-cyan-50 dark:data-[state=active]:bg-cyan-500/10 rounded-full">
                  <BarChart3 className="w-4 h-4" /><span className="hidden sm:inline">Close Call</span>
                </TabsTrigger>
              </TabsList>
              <TabsContent value="most" className="space-y-3">
                {mostTrending.length > 0 ? mostTrending.map((p, i) => <PostItem key={p.id} post={p} index={i} />) : <p className="text-center text-gray-500 py-10">No trending posts yet</p>}
              </TabsContent>
              <TabsContent value="controversial" className="space-y-3">
                {controversial.length > 0 ? controversial.map((p, i) => <PostItem key={p.id} post={p} index={i} />) : <p className="text-center text-gray-500 py-10">No controversial posts yet</p>}
              </TabsContent>
              <TabsContent value="close" className="space-y-3">
                {closeCall.length > 0 ? closeCall.map((p, i) => <PostItem key={p.id} post={p} index={i} />) : <p className="text-center text-gray-500 py-10">No close-call posts yet</p>}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>

      {/* Rich Post Detail Dialog */}
      <Dialog open={!!selectedPost} onOpenChange={() => { setSelectedPost(null); setPostDetails(null); }}>
        <DialogContent className="max-w-lg dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-white">Post Details</DialogTitle>
          </DialogHeader>
          {selectedPost && (
            <div className="space-y-4">
              {/* Author */}
              <div className="flex items-center gap-3">
                <img src={selectedPost.author_avatar} className="w-10 h-10 rounded-full" alt="" />
                <div>
                  <Link
                    to={user && selectedPost.author_username === user.username ? '/profile' : `/profile/${selectedPost.author_username}`}
                    className="font-semibold text-gray-900 dark:text-white hover:underline hover:text-cyan-600"
                    onClick={() => setSelectedPost(null)}
                  >
                    {selectedPost.author_username}
                  </Link>
                  <p className="text-xs text-gray-500 dark:text-slate-400">{getTimeAgo(selectedPost.created_at)}</p>
                </div>
              </div>

              {/* Content */}
              <p className="text-gray-900 dark:text-slate-100 whitespace-pre-wrap p-4 bg-gray-50 dark:bg-slate-700 rounded-xl leading-relaxed">
                {selectedPost.content}
              </p>

              {/* Vote stats */}
              {detailsLoading ? (
                <p className="text-center text-sm text-gray-400 py-2">Loading stats…</p>
              ) : postDetails ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-slate-300">Votes ({postDetails.votes.total} total)</p>
                  {[
                    { label: 'Agree', val: postDetails.votes.agree, pct: postDetails.votes.agree_percent, color: 'bg-green-500', icon: <ThumbsUp className="w-3 h-3" />, text: 'text-green-600' },
                    { label: 'Disagree', val: postDetails.votes.disagree, pct: postDetails.votes.disagree_percent, color: 'bg-red-500', icon: <ThumbsDown className="w-3 h-3" />, text: 'text-red-600' },
                    { label: 'Neutral', val: postDetails.votes.neutral, pct: postDetails.votes.neutral_percent, color: 'bg-gray-400', icon: <Minus className="w-3 h-3" />, text: 'text-gray-500' },
                  ].map(({ label, val, pct, color, icon, text }) => (
                    <div key={label}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className={`${text} flex items-center gap-1 font-medium`}>{icon}{label} ({val})</span>
                        <span className="font-semibold dark:text-white">{pct ?? 0}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-200 dark:bg-slate-600 rounded-full overflow-hidden">
                        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct ?? 0}%` }} />
                      </div>
                    </div>
                  ))}

                  {/* Comments preview */}
                  {postDetails.comments?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Top Comments</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {postDetails.comments.slice(0, 5).map(c => (
                          <div key={c.id} className="flex gap-2 p-2 bg-gray-50 dark:bg-slate-700 rounded-lg">
                            <img src={c.avatar_url} className="w-7 h-7 rounded-full flex-shrink-0" alt="" />
                            <div>
                              <Link
                                to={user && c.username === user.username ? '/profile' : `/profile/${c.username}`}
                                className="text-xs font-medium text-gray-900 dark:text-white hover:underline"
                                onClick={() => setSelectedPost(null)}
                              >
                                {c.username}
                              </Link>
                              <p className="text-xs text-gray-600 dark:text-slate-300">{c.content}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-slate-400">
                  <span className="flex items-center gap-1"><Users className="w-4 h-4" />{selectedPost.vote_count || 0} votes</span>
                  <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" />{selectedPost.comment_count || 0} comments</span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Trending